import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import styled, { css, keyframes } from "styled-components";
import { Capacitor } from "@capacitor/core";
import MaterialIcon from "../material-icon";
import { sp } from "../support-ui/support-ui";
import { tParams, tStr } from "../../utils/i18n-helpers";

export interface ViewerFile {
    name: string;
    blob: Blob;
    /** MIME type as stored with the upload; the name decides when it is missing. */
    type: string | null;
}

interface FileViewerProps {
    file: ViewerFile;
    phone: boolean;
    onClose: () => void;
    /** Saves the file: a download on the web, the share sheet in the app. */
    onDownload: () => void;
    /** Raised where the viewer opens from a drawer that already sits above the page. */
    zIndex?: number;
}

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2.5;
const ZOOM_STEP = 0.25;
const PAGE_MAX_WIDTH = 720;

const isImage = (file: ViewerFile) =>
    (file.type ?? "").startsWith("image/") || /\.(png|jpe?g|webp|gif)$/i.test(file.name);

const fadeIn = keyframes`from { opacity: 0; }`;
const fadeOut = keyframes`to { opacity: 0; }`;
const rise = keyframes`from { opacity: 0; transform: translateY(12px) scale(.98); }`;

// Solid on the phone: it takes the whole screen, and the page must not show through.
const Stage = styled.div<{ $closing: boolean; $phone: boolean; $z: number }>`
    position: fixed;
    inset: 0;
    z-index: ${({ $z }) => $z};
    display: flex;
    flex-direction: column;
    background: ${({ $phone }) => ($phone ? "rgb(10, 14, 28)" : "rgba(6, 10, 20, 0.92)")};
    animation: ${({ $closing }) =>
        $closing
            ? css`${fadeOut} 250ms ${sp.easeIn} forwards`
            : css`${fadeIn} 300ms ${sp.easeOut} both`};

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const Bar = styled.div<{ $phone: boolean }>`
    flex-shrink: 0;
    display: grid;
    grid-template-columns: ${({ $phone }) => ($phone ? "auto minmax(0, 1fr) auto" : "1fr auto 1fr")};
    align-items: center;
    gap: ${({ $phone }) => ($phone ? 10 : 16)}px;
    min-height: 56px;
    padding: ${({ $phone }) =>
        $phone ? "calc(8px + env(safe-area-inset-top, 0px)) 10px 8px" : "0 16px 0 20px"};
    background: rgba(255, 255, 255, 0.06);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    color: #fff;
    font-family: ${sp.body};
`;

const Name = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 9px;
    min-width: 0;
    font-size: 13.5px;

    b {
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    small {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.55);
        white-space: nowrap;
    }
`;

const Zoom = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 2px;
    height: 34px;
    padding: 0 4px;
    border-radius: 9px;
    background: rgba(255, 255, 255, 0.1);

    button {
        all: unset;
        cursor: pointer;
        width: 30px;
        height: 28px;
        border-radius: 7px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #fff;

        &:hover {
            background: rgba(255, 255, 255, 0.16);
        }
    }

    b {
        min-width: 44px;
        text-align: center;
        font-family: ${sp.display};
        font-size: 12.5px;
        font-weight: 500;
        color: #fff;
        font-variant-numeric: tabular-nums;
    }
`;

const Actions = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
`;

const Action = styled.button`
    all: unset;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 34px;
    padding: 0 12px;
    border-radius: 9px;
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    font-size: 12.5px;
    font-weight: 600;

    &:hover {
        background: rgba(255, 255, 255, 0.2);
    }
`;

const Round = styled.button`
    all: unset;
    cursor: pointer;
    width: 36px;
    height: 36px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    background: rgba(255, 255, 255, 0.14);

    &:hover {
        background: rgba(255, 255, 255, 0.26);
    }
`;

const Scroll = styled.div<{ $phone: boolean; $closing: boolean }>`
    flex: 1;
    min-height: 0;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    padding: ${({ $phone }) => ($phone ? "10px 12px 18px" : "28px 24px 40px")};
    animation: ${({ $closing }) =>
        $closing ? "none" : css`${rise} ${sp.dIn} ${sp.easeOut} both`};

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const Pages = styled.div<{ $phone: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${({ $phone }) => ($phone ? 10 : 18)}px;
    width: 100%;
    max-width: ${({ $phone }) => ($phone ? "none" : `${PAGE_MAX_WIDTH}px`)};
    margin: 0 auto;

    canvas,
    img {
        display: block;
        background: #fff;
        border-radius: ${({ $phone }) => ($phone ? 6 : 4)}px;
        box-shadow: 0 24px 70px rgba(0, 0, 0, 0.55);
    }

    img {
        max-width: none;
    }
`;

const Notice = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 80px 16px;
    color: rgba(255, 255, 255, 0.7);
    font-family: ${sp.body};
    font-size: 13px;
    font-weight: 600;
    text-align: center;
`;

const Foot = styled.div`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px calc(12px + env(safe-area-inset-bottom, 0px));
    background: rgba(255, 255, 255, 0.06);

    ${Action} {
        flex: 1;
        justify-content: center;
        height: 44px;
        border-radius: 12px;
        background: ${sp.accent};
        font-size: 14.5px;
    }
`;

/**
 * Draws every page of a PDF onto canvases. pdf.js is loaded on first use only,
 * as the legacy build so older iPhones render it too.
 */
const PdfPages: React.FC<{
    blob: Blob;
    zoom: number;
    phone: boolean;
    onPages: (count: number) => void;
    onError: () => void;
}> = ({ blob, zoom, phone, onPages, onError }) => {
    const host = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const pdfjs = await import("pdfjs-dist/legacy/build/pdf.mjs");
                pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;
                const doc = await pdfjs.getDocument({ data: new Uint8Array(await blob.arrayBuffer()) }).promise;
                if (cancelled) return;
                onPages(doc.numPages);

                const el = host.current;
                if (!el) return;
                el.innerHTML = "";
                const width = Math.min(el.clientWidth, phone ? el.clientWidth : PAGE_MAX_WIDTH) * zoom;
                const ratio = Math.min(window.devicePixelRatio || 1, 2);
                for (let number = 1; number <= doc.numPages; number++) {
                    const page = await doc.getPage(number);
                    if (cancelled) return;
                    const viewport = page.getViewport({ scale: width / page.getViewport({ scale: 1 }).width });
                    const canvas = document.createElement("canvas");
                    canvas.width = viewport.width * ratio;
                    canvas.height = viewport.height * ratio;
                    canvas.style.width = `${viewport.width}px`;
                    canvas.style.height = `${viewport.height}px`;
                    el.appendChild(canvas);
                    const context = canvas.getContext("2d");
                    if (!context) continue;
                    await page.render({
                        canvasContext: context,
                        viewport,
                        transform: ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : undefined,
                    }).promise;
                }
            } catch (error) {
                console.log("Could not render the PDF preview:", error);
                if (!cancelled) onError();
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [blob, zoom, phone, onPages, onError]);

    return <Pages ref={host} $phone={phone} />;
};

/** Shows an attachment in place: PDF pages or the image, with zoom, open and download. */
const FileViewer: React.FC<FileViewerProps> = ({ file, phone, onClose, onDownload, zIndex = 2000 }) => {
    const { t } = useTranslation();
    const [zoom, setZoom] = useState(1);
    const [pages, setPages] = useState<number | null>(null);
    const [failed, setFailed] = useState(false);
    const [closing, setClosing] = useState(false);
    const image = isImage(file);
    const url = useMemo(() => URL.createObjectURL(file.blob), [file.blob]);

    useEffect(() => () => URL.revokeObjectURL(url), [url]);

    const close = useCallback(() => {
        if (closing) return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            onClose();
            return;
        }
        setClosing(true);
        window.setTimeout(onClose, 250);
    }, [closing, onClose]);

    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if (event.key === "Escape") close();
            if (event.key === "+" || event.key === "=") setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP));
            if (event.key === "-") setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP));
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [close]);

    // The app's web view cannot open a blob in a tab; there the share sheet opens it.
    const open = () => {
        if (Capacitor.isNativePlatform()) onDownload();
        else window.open(url, "_blank", "noopener");
    };

    const onPages = useCallback((count: number) => setPages(count), []);
    const onError = useCallback(() => setFailed(true), []);

    const meta = image
        ? tStr(t, "pages.requests.viewer.image")
        : pages === null
          ? "PDF"
          : pages === 1
            ? tStr(t, "pages.requests.viewer.page_single")
            : tParams(t, "pages.requests.viewer.page_multiple", { count: pages });

    const zoomControl = (
        <Zoom>
            <button
                type="button"
                aria-label={tStr(t, "pages.requests.viewer.zoom_out")}
                onClick={() => setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP))}
            >
                <MaterialIcon icon="remove" size={18} color="#fff" />
            </button>
            <b>{Math.round(zoom * 100)}%</b>
            <button
                type="button"
                aria-label={tStr(t, "pages.requests.viewer.zoom_in")}
                onClick={() => setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))}
            >
                <MaterialIcon icon="add" size={18} color="#fff" />
            </button>
        </Zoom>
    );

    const body = failed ? (
        <Notice>
            <MaterialIcon icon="picture_as_pdf" size={30} color="rgba(255,255,255,.6)" />
            {t("pages.requests.viewer.error")}
            <Action type="button" onClick={onDownload}>
                <MaterialIcon icon="download" size={17} color="#fff" />
                {t("pages.requests.viewer.download")}
            </Action>
        </Notice>
    ) : image ? (
        <Pages $phone={phone}>
            <img src={url} alt={file.name} style={{ width: `${100 * zoom}%` }} />
        </Pages>
    ) : (
        <PdfPages blob={file.blob} zoom={zoom} phone={phone} onPages={onPages} onError={onError} />
    );

    const name = (
        <Name>
            {!phone && (
                <MaterialIcon icon={image ? "image" : "picture_as_pdf"} size={18} color="rgba(255,255,255,.7)" />
            )}
            <b>{file.name}</b>
            <small>{meta}</small>
        </Name>
    );

    return ReactDOM.createPortal(
        <Stage $closing={closing} $phone={phone} $z={zIndex} onClick={close} role="dialog" aria-modal="true" aria-label={file.name}>
            {phone ? (
                <Bar $phone onClick={(event) => event.stopPropagation()}>
                    <Round type="button" aria-label={tStr(t, "pages.requests.viewer.close")} onClick={close}>
                        <MaterialIcon icon="close" size={20} color="#fff" />
                    </Round>
                    {name}
                    <Round type="button" aria-label={tStr(t, "pages.requests.viewer.download")} onClick={onDownload}>
                        <MaterialIcon icon="download" size={19} color="#fff" />
                    </Round>
                </Bar>
            ) : (
                <Bar $phone={false} onClick={(event) => event.stopPropagation()}>
                    {name}
                    {zoomControl}
                    <Actions>
                        <Action type="button" onClick={open}>
                            <MaterialIcon icon="open_in_new" size={17} color="#fff" />
                            {t("pages.requests.viewer.open")}
                        </Action>
                        <Action type="button" onClick={onDownload}>
                            <MaterialIcon icon="download" size={17} color="#fff" />
                            {t("pages.requests.viewer.download")}
                        </Action>
                        <Round type="button" aria-label={tStr(t, "pages.requests.viewer.close")} onClick={close}>
                            <MaterialIcon icon="close" size={20} color="#fff" />
                        </Round>
                    </Actions>
                </Bar>
            )}
            <Scroll
                $phone={phone}
                $closing={closing}
                onClick={(event) => {
                    event.stopPropagation();
                    // The dark space around the pages closes, like the backdrop does.
                    if (event.target === event.currentTarget) close();
                }}
            >
                {body}
            </Scroll>
            {phone && (
                <Foot onClick={(event) => event.stopPropagation()}>
                    {zoomControl}
                    <Action type="button" onClick={open}>
                        <MaterialIcon icon="open_in_new" size={18} color="#fff" />
                        {t("pages.requests.viewer.open")}
                    </Action>
                </Foot>
            )}
        </Stage>,
        document.body
    );
};

export default FileViewer;
